import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@client/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@client/components/ui/dropdown-menu';
import { Button } from '@client/components/ui/button';
import { MoreHorizontal, LogOut } from 'lucide-react';
import { format } from 'date-fns';

interface MembershipTableProps {
  supervisorRelationships: SupervisorAdvisorRelationship[] | null;
  onLeave: (relationship: SupervisorAdvisorRelationship) => void;
  /** When true, the leave action is disabled (admin mode) */
  isLeaveDisabled?: boolean;
}

export function MembershipTable({
  supervisorRelationships,
  onLeave,
  isLeaveDisabled = false,
}: MembershipTableProps) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Owner</TableHead>
            <TableHead>Client Access</TableHead>
            <TableHead>Start Date</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!supervisorRelationships || supervisorRelationships.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={4}
                className="py-12 text-center"
              >
                <p className="text-gray-700">You are not a member of any team.</p>
              </TableCell>
            </TableRow>
          ) : (
            supervisorRelationships.map((relationship, idx) => (
              <TableRow key={relationship.advisorId || idx}>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-900">
                      {relationship.advisorFirstName && relationship.advisorLastName
                        ? `${relationship.advisorFirstName} ${relationship.advisorLastName}`
                        : relationship.advisorEmail}
                    </span>
                    <span className="text-sm text-gray-700">{relationship.advisorEmail}</span>
                    {relationship.organizationDisplayName || relationship.organizationName ? (
                      <span className="text-sm text-gray-700">
                        {relationship.organizationDisplayName || relationship.organizationName}
                      </span>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className="text-gray-900">
                  {relationship.enabledClientAccess ? 'Yes' : 'No'}
                </TableCell>
                <TableCell className="text-gray-900">
                  {relationship.createdDateTime
                    ? format(new Date(relationship.createdDateTime), 'MMM d, yyyy')
                    : '—'}
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        disabled={isLeaveDisabled}
                      >
                        <MoreHorizontal className="h-4 w-4 text-gray-900" />
                        <span className="sr-only">Open actions menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => onLeave(relationship)}
                        disabled={isLeaveDisabled}
                        className={isLeaveDisabled ? 'cursor-not-allowed opacity-50' : ''}
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        {isLeaveDisabled ? 'Leave Team (Disabled in Admin Mode)' : 'Leave Team'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
